import { useId } from "react";
import { cn } from "../../../lib/utils";
import { ZONE_MAP_BACKGROUND, ZONE_MAP_SHAPES, ZONE_MAP_VIEWBOX } from "./zone-map-geometry";
import "./zone-map.css";

// The EEX Research map of European bidding zones. It is presentational: each
// view decides what a zone means (stored, priced, compared...) and owns its
// own selection, so Research sub-tabs never share state through the map.
export type ZoneMapTone = "stored" | "partial" | "progress" | "failed" | "available" | "unavailable";

export interface ZoneMapZone {
  shapeId: string;
  // Shown in the hover title, e.g. "Austria · Stored".
  title: string;
  tone: ZoneMapTone;
  // A CSS colour that overrides the tone fill, for choropleth views.
  fill?: string;
}

export interface ZoneMapProps {
  // Accessible summary of what the map shows.
  label: string;
  zones: readonly ZoneMapZone[];
  activeShapeId?: string | null;
  onZoneSelect?: (shapeId: string) => void;
  showLabels?: boolean;
  className?: string;
}

export const ZONE_MAP_SHAPE_IDS: readonly string[] = ZONE_MAP_SHAPES.map((shape) => shape.id);
const [, , VIEW_WIDTH, VIEW_HEIGHT] = ZONE_MAP_VIEWBOX.split(" ").map(Number);
// Lets a view size the map by height alone.
const ASPECT_RATIO = `${VIEW_WIDTH} / ${VIEW_HEIGHT}`;

export function ZoneMap({ label, zones, activeShapeId = null, onZoneSelect, showLabels = true, className }: ZoneMapProps) {
  const hatchId = `zone-map-hatch-${useId().replace(/:/g, "")}`;
  const byShape = new Map(zones.map((zone) => [zone.shapeId, zone]));
  const active = activeShapeId ? ZONE_MAP_SHAPES.find((shape) => shape.id === activeShapeId) : undefined;
  return (
    // Pointer shortcut only: every zone action is also reachable from the
    // view's own list, so the drawing is one labelled image for assistive tech.
    <svg viewBox={ZONE_MAP_VIEWBOX} role="img" aria-label={label} className={cn("zone-map", className)} style={{ aspectRatio: ASPECT_RATIO }}>
      <defs>
        <pattern id={hatchId} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="8" height="8" className="zone-map-hatch-bg" />
          <line x1="0" y1="0" x2="0" y2="8" className="zone-map-hatch-line" />
        </pattern>
      </defs>
      {ZONE_MAP_BACKGROUND.map((shape) => <path key={shape.id} d={shape.d} className="zone-map-context" />)}
      {ZONE_MAP_SHAPES.map((shape) => {
        const zone = byShape.get(shape.id);
        if (!zone) return <path key={shape.id} d={shape.d} className="zone-map-context" />;
        const selectable = Boolean(onZoneSelect);
        return (
          <path
            key={shape.id}
            d={shape.d}
            data-zone={shape.id}
            data-tone={zone.tone}
            className={cn("zone-map-zone", `zone-map-${zone.tone}`, selectable && "zone-map-selectable")}
            style={zone.fill ? { fill: zone.fill } : zone.tone === "unavailable" ? { fill: `url(#${hatchId})` } : undefined}
            onClick={selectable ? () => onZoneSelect!(shape.id) : undefined}
          >
            <title>{zone.title}</title>
          </path>
        );
      })}
      {active && <path d={active.d} className="zone-map-active" />}
      {showLabels && ZONE_MAP_SHAPES.map((shape) =>
        shape.label && byShape.has(shape.id)
          ? <text key={shape.id} x={shape.label[0]} y={shape.label[1]} className="zone-map-label">{shape.id}</text>
          : null)}
    </svg>
  );
}

export function ZoneMapLegend({ items, className }: { items: readonly { tone: ZoneMapTone; label: string }[]; className?: string }) {
  const hatchId = `zone-map-legend-hatch-${useId().replace(/:/g, "")}`;
  return (
    <ul className={cn("zone-map-legend", className)}>
      {items.map((item) => (
        <li key={item.tone}>
          <svg viewBox="0 0 14 14" aria-hidden="true">
            {item.tone === "unavailable" && (
              <defs>
                <pattern id={hatchId} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="4" height="4" className="zone-map-hatch-bg" />
                  <line x1="0" y1="0" x2="0" y2="4" className="zone-map-hatch-line" />
                </pattern>
              </defs>
            )}
            <rect width="14" height="14" rx="3" className={cn("zone-map-zone", `zone-map-${item.tone}`)}
              style={item.tone === "unavailable" ? { fill: `url(#${hatchId})` } : undefined} />
          </svg>
          {item.label}
        </li>
      ))}
    </ul>
  );
}
